import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { getMessaging, getToken, onMessage } from 'firebase/messaging'

import { useAppDispatch, useAppSelector } from '../store'
import { useGetMeQuery, useGetMarketsQuery, useGetOutletsQuery, useRegisterPushTokenMutation } from '../store/api/api'
import { slice as authSlice } from '../store/auth'
import { slice as marketSlice } from '../store/market'

const LOGIN_ROUTE = '/login'

// Загрузка пользователя вынесена в отдельный компонент, который монтируется ТОЛЬКО
// пока статус 'checking'. Тогда getMe стартует на маунте — тот же путь, что и при
// перезагрузке страницы (которая работает). Если держать getMe в родителе со skip,
// то при SPA-логине (loggedOut → checking) запрос не доводится до конца и экран
// застревает на «...» — приходится перезагружать вручную.
const CheckingUser: React.FC = () => {
  const dispatch = useAppDispatch()
  const { data: user } = useGetMeQuery()

  React.useEffect(() => {
    if (user) {
      dispatch(authSlice.actions.userLoadSuccess(user))
    }
  }, [user, dispatch])

  return (
    <div>
      <p>...</p>
    </div>
  )
}

interface Props {
  children: React.ReactNode
}

const RefreshToken: React.FC<Props> = ({ children }) => {
  const location = useLocation()

  const dispatch = useAppDispatch()
  const auth = useAppSelector((state) => state.auth)
  const market = useAppSelector((state) => state.market)

  const [registerPushToken] = useRegisterPushTokenMutation()

  const { data: markets } = useGetMarketsQuery(undefined, {
    skip: auth.status !== 'loggedIn',
  })

  const { data: outlets } = useGetOutletsQuery(market.selected!, {
    skip: market.selected == null,
  })

  const isLoginRoute = location.pathname === LOGIN_ROUTE

  React.useEffect(() => {
    if (markets) {
      dispatch(marketSlice.actions.setMarkets(markets))
    }
  }, [markets, dispatch])

  React.useEffect(() => {
    if (outlets) {
      dispatch(marketSlice.actions.setOutlets(outlets))
    }
  }, [outlets, dispatch])

  React.useEffect(() => {
    if (auth.user && 'serviceWorker' in navigator) {
      const userCode = auth.user.code
      const messaging = getMessaging()

      navigator.serviceWorker
        .getRegistration()
        .then((reg) => getToken(messaging, { serviceWorkerRegistration: reg }))
        .then((token) => {
          if (token) {
            registerPushToken({ token, platform: 'Web' })
            // Сохраняем токен в стор, чтобы при выходе его можно было отозвать (см. Profile.logout)
            if (userCode != null) {
              dispatch(authSlice.actions.saveTokenStart({ userId: userCode, token }))
            }
            onMessage(messaging, (payload) => {
              console.log('Message received. ', payload)
            })
          } else {
            console.log('No registration token available. Request permission to generate one.')
          }
        })
        .catch((err) => {
          console.log('Unable to get permission to notify', err)
        })
    }
  }, [auth.user, registerPushToken])

  return auth.status === 'checking' ? (
    <CheckingUser />
  ) : isLoginRoute && auth.status === 'loggedIn' ? (
    <Navigate to="/market" replace />
  ) : !isLoginRoute && auth.status === 'loggedOut' ? (
    <Navigate to={LOGIN_ROUTE} replace />
  ) : (
    <>{children}</>
  )
}

export default RefreshToken
